<template>
  <div v-if="currentUser && customizations" class="invoices-tab">
    <Transition>
      <div v-if="currentInvoice">
        <InvoiceHeader
          :invoice="currentInvoice"
          @refresh-invoice="refreshInvoice"
        >
          <template #title>
            <h3>
              <Breadcrumbs
                :crumbs="crumbs"
                root-text="Invoices"
                @click="handleCrumbClick"
              />
            </h3>
          </template>
        </InvoiceHeader>

        <Invoice
          v-if="currentInvoice"
          class="ssui-invoice"
          :invoice="currentInvoice"
          :customizations="customizations"
          :equipment-types-enabled="equipmentTypesEnabled"
          :tenant-timezone="tenantTimezone"
          @edit-transaction-note-click="handleEditNoteClick"
          @edit-transaction-reference-id-click="handleEditReferenceIdClick"
          @equipment-request-click="toggleModal('captureModal')"
        />
        <EditTransactionNotesModal
          v-if="editNotesModal"
          :transaction="selectedTransaction"
          @hide-modal="clearNotesModal"
        />
        <TransactionReferenceIdModal
          v-if="editReferenceIdModal"
          :transaction="selectedTransaction"
          @hide-modal="clearReferenceIdModal"
        />
        <EquipmentCaptureModal
          v-if="captureModal"
          :invoice="currentInvoice"
          :address="currentInvoice.service_address"
          :equipment="customerEquipment"
          @hide-modal="toggleModal('captureModal')"
          @refresh-invoice="refreshInvoice"
        />
      </div>
      <div v-else-if="!loading">
        <CustomerInvoicesDatatable
          ref="invoicesTable"
          :filters="filters"
          flat
          :source="source"
          :customer-id="currentUser.id"
          :address="address"
          :show-sub-customers="currentUser.has_subs"
          @row-click="handleClick"
          @view-invoice="viewInvoice"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Transition from "@/components/Transition.vue";
import EquipmentCaptureModal from "@/Equipment/EquipmentCaptureModal.vue";
import InvoiceHeader from "@/Invoice/InvoiceHeader.vue";
import EditTransactionNotesModal from "@/Job/EditTransactionNotesModal.vue";
import TransactionReferenceIdModal from "@/Job/TransactionReferenceIdModal.vue";
import CustomerInvoicesDatatable from "./CustomerInvoicesDatatable.vue";

export default {
  components: {
    CustomerInvoicesDatatable,
    Transition,
    EditTransactionNotesModal,
    TransactionReferenceIdModal,
    InvoiceHeader,
    EquipmentCaptureModal
  },
  props: {
    address: {
      type: Object,
      default: null
    },
    filters: {
      type: Array,
      default: () => []
    },
    source: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: false,
      crumbs: [],
      editNotesModal: false,
      editReferenceIdModal: false,
      selectedTransaction: null,
      captureModal: false
    };
  },
  computed: {
    ...mapState({
      currentUser: "currentUser",
      currentInvoice: "currentInvoice",
      customerEquipment: "customerEquipment",
      customizations: ({ tenantCustomizations, tenant }) => {
        if (!tenant || !tenantCustomizations) {
          return null;
        }
        return {
          ...tenantCustomizations,
          Tenant: { ...tenant }
        };
      },
      equipmentTypesEnabled({ tenant }) {
        if (!tenant) return false;
        return tenant.feature_flags.equipment_types;
      },
      tenantTimezone({ tenant }) {
        if (!tenant) return "";
        return tenant.timezone;
      }
    })
  },
  watch: {
    currentInvoice(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleInvoiceChange();
      }
    }
  },
  async created() {
    const params = getQueryVariables();
    if (params.invoice_id) {
      await Promise.all([
        this.loadInvoice(params.invoice_id),
        this.fetchTenantCustomizations()
      ]);
    }
    if (!this.tenant && !this.fetchingTenant) {
      await this.fetchTenant();
    }
    window.addEventListener("popstate", this.navigateBack);
    EventBus.$on("refresh-invoice", this.refreshInvoice);
  },
  beforeUnmount() {
    window.removeEventListener("popstate", this.navigateBack);
    this.setCurrentInvoice(null);
    EventBus.$off("refresh-invoice", this.refreshInvoice);
  },
  methods: {
    ...mapActions([
      "fetchInvoice",
      "fetchJob",
      "fetchTenant",
      "setCurrentInvoice",
      "fetchTenantCustomizations"
    ]),
    getCrumbTitle() {
      return `Invoice #${this.currentInvoice.invoice_id}`;
    },
    handleClick(row) {
      this.loadInvoice(row.invoice_id);
    },
    viewInvoice(id) {
      this.loadInvoice(id);
    },
    async loadInvoice(id) {
      this.loading = true;
      try {
        await this.fetchInvoice(id);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching invoice", error);
      } finally {
        this.loading = false;
      }
    },
    handleCrumbClick(crumb) {
      if (crumb.id === "root") {
        this.setCurrentInvoice(null);
      }
    },
    handleInvoiceChange() {
      if (this.currentInvoice) {
        const { cancelled, voided, kind, invoice_id } = this.currentInvoice;
        const title = this.getCrumbTitle();
        const className = "";
        this.crumbs = [
          {
            id: invoice_id,
            label: title,
            className
          }
        ];
        updateQueryParam("invoice_id", invoice_id);
      } else {
        updateQueryParam("invoice_id", null);
      }
    },
    handleEditNoteClick(transaction) {
      this.selectedTransaction = transaction;
      this.editNotesModal = true;
    },
    handleEditReferenceIdClick(transaction) {
      this.selectedTransaction = transaction;
      this.editReferenceIdModal = true;
    },
    clearNotesModal() {
      this.editNotesModal = false;
      this.selectedTransaction = null;
    },
    clearReferenceIdModal() {
      this.editReferenceIdModal = false;
      this.selectedTransaction = null;
    },
    toggleModal(modal) {
      this[modal] = !this[modal];
    },
    refreshInvoiceList() {
      this.$refs.invoicesTable?.refresh();
    },
    refreshInvoice() {
      if (this.currentInvoice) {
        this.loadInvoice(this.currentInvoice.invoice_id);
      }
    },
    navigateBack() {
      const params = getQueryVariables();
      if (params.invoice_id == undefined) {
        this.setCurrentInvoice(null);
      } else {
        this.viewInvoice(params.invoice_id);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.invoices-tab:empty {
  min-height: 100vh;
}
:deep(.crumb-title-alert .crumb) {
  background: var(--danger-color);
  border-radius: 3px;
  color: #fff;
  padding: 0 3px;
}

// Fixes SSUI invoice header table bottom left/right
// border radius overridden by general.scss stylesheet
.invoices-tab {
  :deep(.header-content .table) {
    margin-bottom: 0;
    border-radius: 5px 5px 0 0;
  }
}

:deep(.invoice-body) {
  .table {
    margin-bottom: 0;
  }
}

:deep {
  .table td {
    border-top: none; // Removes bootstrap default border display
    vertical-align: baseline !important; // Removes bootstrap default column align
  }
}
</style>
